import { NgClass, NgStyle } from '@angular/common';
import { Component, OnInit, inject, input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AppSubjects } from '@pflegix/authorization';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { RadioButtonModule } from 'primeng/radiobutton';
import { MenuService } from '../app.menu.service';
import { ColorScheme, LayoutService, MenuMode } from '../service/app.layout.service';

type Theme = { name: string; color: string };

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-config',
  templateUrl: './app.config.component.html',
  imports: [FormsModule, DrawerModule, RadioButtonModule, ButtonModule, NgStyle, NgClass],
})
export class AppConfigComponent implements OnInit {
  private readonly layoutService = inject(LayoutService);
  private readonly menuService = inject(MenuService);

  public readonly minimal = input(false);

  public componentThemes: Theme[] = [];

  public menuThemes: Theme[] = [];

  public topbarThemes: Theme[] = [];

  public scales: number[] = [12, 13, 14, 15, 16];

  protected readonly appSubjects = AppSubjects;

  public get visible(): boolean {
    return this.layoutService.state.configSidebarVisible;
  }

  public set visible(_val: boolean) {
    this.layoutService.state.configSidebarVisible = _val;
  }

  public get scale(): number {
    return this.layoutService.config().scale;
  }

  public set scale(_val: number) {
    this.layoutService.config.update((config) => ({
      ...config,
      scale: _val,
    }));
  }

  public get menuMode(): MenuMode {
    return this.layoutService.config().menuMode;
  }

  public set menuMode(_val: MenuMode) {
    this.layoutService.config.update((config) => ({
      ...config,
      menuMode: _val,
    }));
    if (this.layoutService.isSlim() || this.layoutService.isHorizontal()) {
      this.menuService.reset();
    }
  }

  public get menuProfilePosition(): string {
    return this.layoutService.config().menuProfilePosition;
  }

  public set menuProfilePosition(_val: string) {
    this.layoutService.config.update((config) => ({
      ...config,
      menuProfilePosition: _val,
    }));
    if (this.layoutService.isSlimPlus() || this.layoutService.isSlim() || this.layoutService.isHorizontal()) {
      this.menuService.reset();
    }
  }

  public get colorScheme(): ColorScheme {
    return this.layoutService.config().colorScheme;
  }

  public set colorScheme(_val: ColorScheme) {
    this.layoutService.config.update((config) => ({
      ...config,
      menuTheme: _val === 'dark' ? 'dark' : config.menuTheme,
      colorScheme: _val,
    }));
  }

  public get inputStyle(): string {
    return this.layoutService.config().inputStyle;
  }

  public set inputStyle(_val: string) {
    this.layoutService.config.update((config) => ({
      ...config,
      inputStyle: _val,
    }));
  }

  public get menuTheme(): string {
    return this.layoutService.config().menuTheme;
  }

  public set menuTheme(val: string) {
    this.layoutService.config.update((config) => ({
      ...config,
      menuTheme: val,
    }));
  }

  public get topbarTheme(): string {
    return this.layoutService.config().topbarTheme;
  }

  public set topbarTheme(val: string) {
    this.layoutService.config.update((config) => ({
      ...config,
      topbarTheme: val,
    }));
  }

  public get componentTheme(): string {
    return this.layoutService.config().componentTheme;
  }

  public set componentTheme(val: string) {
    this.layoutService.config.update((config) => ({
      ...config,
      componentTheme: val,
    }));
  }

  public ngOnInit(): void {
    this.componentThemes = [
      { name: 'indigo', color: '#3F51B5' },
      { name: 'pink', color: '#E91E63' },
      { name: 'purple', color: '#9C27B0' },
      { name: 'deeppurple', color: '#673AB7' },
      { name: 'blue', color: '#2196F3' },
      { name: 'lightblue', color: '#03A9F4' },
      { name: 'cyan', color: '#00BCD4' },
      { name: 'teal', color: '#009688' },
      { name: 'green', color: '#4CAF50' },
      { name: 'lightgreen', color: '#8BC34A' },
      { name: 'lime', color: '#CDDC39' },
      { name: 'yellow', color: '#FFEB3B' },
      { name: 'amber', color: '#FFC107' },
      { name: 'orange', color: '#FF9800' },
      { name: 'deeporange', color: '#FF5722' },
      { name: 'brown', color: '#795548' },
      { name: 'bluegrey', color: '#607D8B' },
    ];

    this.menuThemes = [
      { name: 'light', color: '#FDFEFF' },
      { name: 'dark', color: '#434B54' },
      { name: 'indigo', color: '#1A237E' },
      { name: 'bluegrey', color: '#37474F' },
      { name: 'brown', color: '#4E342E' },
      { name: 'cyan', color: '#006064' },
      { name: 'green', color: '#2E7D32' },
      { name: 'deeppurple', color: '#4527A0' },
      { name: 'deeporange', color: '#BF360C' },
      { name: 'pink', color: '#880E4F' },
      { name: 'purple', color: '#6A1B9A' },
      { name: 'teal', color: '#00695C' },
    ];

    this.topbarThemes = [
      { name: 'lightblue', color: '#2E88FF' },
      { name: 'dark', color: '#363636' },
      { name: 'white', color: '#FDFEFF' },
      { name: 'blue', color: '#1565C0' },
      { name: 'deeppurple', color: '#4527A0' },
      { name: 'purple', color: '#6A1B9A' },
      { name: 'pink', color: '#AD1457' },
      { name: 'cyan', color: '#0097A7' },
      { name: 'teal', color: '#00796B' },
      { name: 'green', color: '#43A047' },
      { name: 'lightgreen', color: '#689F38' },
      { name: 'lime', color: '#AFB42B' },
      { name: 'yellow', color: '#FBC02D' },
      { name: 'amber', color: '#FFA000' },
      { name: 'orange', color: '#FB8C00' },
      { name: 'deeporange', color: '#D84315' },
      { name: 'brown', color: '#5D4037' },
      { name: 'grey', color: '#616161' },
      { name: 'bluegrey', color: '#546E7A' },
      { name: 'indigo', color: '#3F51B5' },
    ];
  }

  public onConfigButtonClick(): void {
    this.layoutService.showConfigSidebar();
  }

  public changeTheme(theme: string): void {
    this.componentTheme = theme;
  }

  public changeTopbarTheme(theme: string): void {
    this.topbarTheme = theme;
  }

  public changeMenuTheme(theme: string): void {
    this.menuTheme = theme;
  }

  public decrementScale(): void {
    this.scale -= 1;
  }

  public incrementScale(): void {
    this.scale += 1;
  }
}
